import { osnovaApi } from '../base';
import { parseResponse } from '../parseResponse';
import { parseFilenameFromDisposition } from '@shared/lib/downloadFile';
import type { FieldInspectionRequest } from '@entities/field/types';
import type { FunctionRequest } from '@entities/requests/types';
import type { PackageDetail, PtoAct, PtoActTemplate, PtoAosrRequisites, PtoAosrRequisitesResponse, PtoChatMessage, PtoChatSession, PtoChecklist, PtoChecklistDetail, PtoChecklistTemplate, PtoEquipment, PtoEquipmentBody, PtoEquipmentBulkResult, PtoEquipmentImport, PtoExecutiveAct, PtoIdPackage, PtoInfo, PtoInspection, PtoLetterTemplate, PtoMaterialsSummary, PtoMember, PtoOrderRepresentative, PtoPprFigure, PtoPprGenerateBody, PtoPprRdSource, PtoPprTkChunk, PtoPprTkDoc, PtoPprTkDraft, PtoProject, PtoQualityMaterial, PtoRdCarryOver, PtoRdCarryOverApplied, PtoRdCarryOverPair, PtoRdDocument, PtoRdRevision, PtoRefreshSectionsResult, PtoRequest, PtoSmrItem, PtoSmrRegister, PtoVolumeCheck, PtoVolumeDiscrepancy, PtoVolumePosition, RdChangeProtocol } from '../types';

/**
 * Файл DOCX: успешный ответ — `File` с именем из `Content-Disposition` (если
 * сервер прислал), иначе `Blob`. Ошибку (409 «чистовой — только после
 * утверждения») разбираем как JSON, чтобы UI показал причину, а не «[object Blob]».
 */
async function fileResponse(response: Response): Promise<unknown> {
  if (!response.ok) return parseResponse(response);
  const blob = await response.blob();
  const name = parseFilenameFromDisposition(response.headers.get('content-disposition'));
  return name ? new File([blob], name, { type: blob.type }) : blob;
}

export const ptoApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    getPtoInfo: b.query<PtoInfo, void>({
      query: () => '/agents/pto/info',
    }),
    /** `limit` — сколько проектов отдать (по умолчанию сервер отдаёт 50). */
    listPtoProjects: b.query<
      { items: PtoProject[] },
      { deleted?: boolean; limit?: number } | void
    >({
      query: (arg) => {
        const params = new URLSearchParams();
        if (arg && arg.deleted) params.set('deleted', 'true');
        if (arg && arg.limit) params.set('limit', String(arg.limit));
        const qs = params.toString();
        return `/agents/pto/projects${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['PtoProject'],
    }),
    createPtoProject: b.mutation<{ project: PtoProject }, { name: string; code?: string }>({
      query: (body) => ({ url: '/agents/pto/projects', method: 'POST', body }),
      invalidatesTags: ['PtoProject'],
    }),
    getPtoProject: b.query<{ project: PtoProject }, string>({
      query: (id) => `/agents/pto/projects/${id}`,
      providesTags: ['PtoProject'],
    }),
    archivePtoProject: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/pto/projects/${id}/archive`, method: 'POST' }),
      invalidatesTags: ['PtoProject'],
    }),
    /** Пометить проект на удаление (soft-delete). Роли engineer_plus | admin. */
    deletePtoProject: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/pto/projects/${id}/delete`, method: 'POST' }),
      invalidatesTags: ['PtoProject'],
    }),
    restorePtoProject: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/pto/projects/${id}/restore`, method: 'POST' }),
      invalidatesTags: ['PtoProject'],
    }),
    listPtoMembers: b.query<{ items: PtoMember[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/members`,
    }),
    addPtoMember: b.mutation<
      { member: PtoMember },
      { projectId: string; user_id: string; role: string }
    >({
      query: ({ projectId, user_id, role }) => ({
        url: `/agents/pto/projects/${projectId}/members`,
        method: 'POST',
        body: { user_id, role },
      }),
    }),
    listPtoRdDocuments: b.query<
      { items: PtoRdDocument[] },
      { projectId: string; deleted?: boolean }
    >({
      async queryFn({ projectId, deleted }, api, _extraOptions, baseQuery) {
        const limit = 50;
        const items: PtoRdDocument[] = [];
        for (let offset = 0; ; offset += limit) {
          if (api.signal.aborted) {
            return { error: { status: 'CUSTOM_ERROR', error: 'Загрузка документов РД отменена' } };
          }
          const result = await baseQuery({
            url: `/agents/pto/projects/${projectId}/rd-documents`,
            params: { limit, offset, ...(deleted ? { deleted: true } : {}) },
          });
          if (result.error) return { error: result.error };
          if (api.signal.aborted) {
            return { error: { status: 'CUSTOM_ERROR', error: 'Загрузка документов РД отменена' } };
          }
          const page = result.data as { items: PtoRdDocument[] };
          items.push(...page.items);
          if (page.items.length < limit) return { data: { items } };
        }
      },
      providesTags: ['PtoRdDocument'],
    }),
    deletePtoRdDocument: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/pto/rd-documents/${id}/delete`, method: 'POST' }),
      invalidatesTags: ['PtoRdDocument'],
    }),
    restorePtoRdDocument: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/pto/rd-documents/${id}/restore`, method: 'POST' }),
      invalidatesTags: ['PtoRdDocument'],
    }),
    createPtoRdDocument: b.mutation<
      { document: PtoRdDocument },
      { projectId: string; code: string; mark?: string; title: string; discipline?: string }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/rd-documents`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoRdDocument'],
    }),
    /** Загрузка ревизии РД — multipart FormData (поле `rev_label` + файлы). */
    uploadPtoRevision: b.mutation<
      { revision: PtoRdRevision },
      { projectId: string; documentId: string; form: FormData }
    >({
      query: ({ projectId, documentId, form }) => ({
        url: `/agents/pto/projects/${projectId}/rd-documents/${documentId}/revisions`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['PtoRdRevision'],
    }),
    listPtoRevisions: b.query<
      { items: PtoRdRevision[] },
      { documentId: string; deleted?: boolean }
    >({
      query: ({ documentId, deleted }) =>
        `/agents/pto/rd-documents/${documentId}/revisions${deleted ? '?deleted=true' : ''}`,
      providesTags: ['PtoRdRevision'],
    }),
    deletePtoRevision: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/pto/revisions/${id}/delete`, method: 'POST' }),
      invalidatesTags: ['PtoRdRevision'],
    }),
    restorePtoRevision: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/pto/revisions/${id}/restore`, method: 'POST' }),
      invalidatesTags: ['PtoRdRevision'],
    }),
    getPtoRevision: b.query<{ revision: PtoRdRevision }, string>({
      query: (id) => `/agents/pto/revisions/${id}`,
    }),
    reindexPtoRevision: b.mutation<
      { ok: boolean; status: string },
      { id: string; ocr?: boolean }
    >({
      query: ({ id, ocr }) => ({
        url: `/agents/pto/revisions/${id}/reindex${ocr ? '?ocr=true' : ''}`,
        method: 'POST',
      }),
      invalidatesTags: ['PtoRdRevision'],
    }),
    /** «Обновить разделы и номера» по проекту (только администратор): по задаче в
     *  очереди ПТО на каждую действующую ревизию — перечитать основные надписи
     *  листов, записать позициям код части РД и подпись «Раздела», пронумеровать
     *  строки. Позиции, количества и привязки не меняются. */
    refreshPtoProjectSections: b.mutation<PtoRefreshSectionsResult, string>({
      query: (projectId) => ({
        url: `/agents/pto/projects/${projectId}/refresh-sections`,
        method: 'POST',
      }),
      invalidatesTags: ['PtoRdRevision', 'PtoVolume'],
    }),
    /** Позиции объёма ревизии (M2.1), в порядке РД. */
    listPtoVolumes: b.query<{ items: PtoVolumePosition[] }, string>({
      query: (revisionId) => `/agents/pto/revisions/${revisionId}/volumes`,
      providesTags: ['PtoVolume'],
    }),
    /** Вид «Сводно ▶» экрана «Объёмы»: ведомость материалов (тот же отбор, что у
     *  Excel) с долями систем строк и перечнем систем комплекта. */
    getPtoVolumesSummary: b.query<PtoMaterialsSummary, string>({
      query: (revisionId) => `/agents/pto/revisions/${revisionId}/volumes/summary`,
      providesTags: ['PtoVolume'],
    }),
    /** Запустить (пере)извлечение объёмов из ревизии. */
    extractPtoVolumes: b.mutation<{ ok: boolean; status: string }, string>({
      query: (revisionId) => ({
        url: `/agents/pto/revisions/${revisionId}/extract-volumes`,
        method: 'POST',
      }),
      invalidatesTags: ['PtoVolume'],
    }),
    /** Новая ревизия РД: позиции заменённых ревизий с привязками Прораба
     *  (элементы, факты, строки ОЖР) и их продолжения в действующей ревизии —
     *  предпросмотр переноса, ничего не меняет. План зависит от ревизий,
     *  позиций и привязок Прораба — обновляется вместе с ними. */
    getPtoRdCarryOver: b.query<PtoRdCarryOver, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/rd-carry-over`,
      providesTags: ['PtoRdCarryOver', 'PtoRdRevision', 'PtoVolume', 'Field'],
    }),
    /** Перенести привязки Прораба на действующую ревизию РД: явные пары или
     *  (без `pairs`) все однозначные продолжения. Меняет конструктив и факты
     *  площадок (сверка пересчитана) и строки ОЖР. */
    applyPtoRdCarryOver: b.mutation<
      PtoRdCarryOver & { applied: PtoRdCarryOverApplied },
      { projectId: string; pairs?: PtoRdCarryOverPair[] }
    >({
      query: ({ projectId, pairs }) => ({
        url: `/agents/pto/projects/${projectId}/rd-carry-over`,
        method: 'POST',
        body: pairs ? { pairs } : undefined,
      }),
      invalidatesTags: ['PtoRdCarryOver', 'PtoVolume', 'Field', 'PtoOzhr'],
    }),
    /** HITL-разметка позиции объёма: accepted | rejected | disputed. */
    annotatePtoVolume: b.mutation<
      { ok: boolean },
      { id: string; decision: string; comment?: string }
    >({
      query: ({ id, decision, comment }) => ({
        url: `/agents/pto/volumes/${id}/annotate`,
        method: 'POST',
        body: { decision, comment },
      }),
      invalidatesTags: ['PtoVolume'],
    }),
    /** Список актов-периодов выполнения (M7). */
    listPtoActs: b.query<{ items: PtoAct[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/acts`,
      providesTags: ['PtoAct'],
    }),
    /** Записать выполнение за период (структурный ввод, M7). `party` (gp|sp) —
     *  опц. сторона для сверки ГП↔СП (M3). */
    recordPtoExecuted: b.mutation<
      { act: PtoAct },
      {
        projectId: string;
        period: string;
        party?: string;
        lines: Array<{
          name: string;
          unit?: string;
          code?: string;
          qty_period?: number;
          qty_rd?: number;
          position_id?: string;
          source_ref?: string;
        }>;
      }
    >({
      query: ({ projectId, period, party, lines }) => ({
        url: `/agents/pto/projects/${projectId}/executed`,
        method: 'POST',
        body: { period, party, lines },
      }),
      invalidatesTags: ['PtoAct'],
    }),
    /** Выгрузка XLSX-отчёта выполнения проекта (M7). Лениво, по клику. */
    exportPtoReportXlsx: b.query<Blob, string>({
      query: (projectId) => ({
        url: `/agents/pto/projects/${projectId}/report-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),
    /** Ведомость всех материалов по ревизии РД (XLSX). Лениво, по клику (M4). */
    exportPtoMaterialsVedomost: b.query<Blob, string>({
      query: (revisionId) => ({
        url: `/agents/pto/revisions/${revisionId}/materials-vedomost-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),
    /** Шаблоны акта заказчика проекта (M4). */
    listPtoActTemplates: b.query<{ items: PtoActTemplate[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/act-templates`,
      providesTags: ['PtoActTemplate'],
    }),
    /** Загрузить .docx-шаблон акта заказчика (multipart) (M4). */
    uploadPtoActTemplate: b.mutation<
      { template: PtoActTemplate },
      { projectId: string; form: FormData }
    >({
      query: ({ projectId, form }) => ({
        url: `/agents/pto/projects/${projectId}/act-templates`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['PtoActTemplate'],
    }),
    /** Удалить шаблон акта заказчика (M4). */
    deletePtoActTemplate: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/pto/act-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PtoActTemplate'],
    }),
    /** Библиотека прошлых ППР/ТК проекта (M9 фаза 1). */
    listPtoPprTk: b.query<{ items: PtoPprTkDoc[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/ppr-tk-library`,
      providesTags: ['PtoPprTk'],
    }),
    /** Загрузить прецедент ППР/ТК (multipart) (M9). */
    uploadPtoPprTk: b.mutation<{ document: PtoPprTkDoc }, { projectId: string; form: FormData }>({
      query: ({ projectId, form }) => ({
        url: `/agents/pto/projects/${projectId}/ppr-tk-library`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['PtoPprTk'],
    }),
    /** Поиск по прецедентам ППР/ТК (лексический) (M9). */
    searchPtoPprTk: b.query<
      { items: PtoPprTkChunk[] },
      { projectId: string; q: string; kind?: string; top_k?: number }
    >({
      query: ({ projectId, q, kind, top_k }) => {
        const p = new URLSearchParams({ q });
        if (kind) p.set('kind', kind);
        if (top_k) p.set('top_k', String(top_k));
        return `/agents/pto/projects/${projectId}/ppr-tk-search?${p.toString()}`;
      },
    }),
    /** Удалить прецедент ППР/ТК из библиотеки (M9). */
    deletePtoPprTk: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/pto/ppr-tk-library/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PtoPprTk'],
    }),
    /** Список черновиков ППР/ТК проекта (M9 фаза 2). */
    listPtoPprTkDrafts: b.query<{ items: PtoPprTkDraft[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/ppr-tk-drafts`,
      providesTags: ['PtoPprTkDraft'],
    }),
    /** Собрать черновик ППР/ТК из прецедентов + LLM (M9 фаза 2). */
    createPtoPprTkDraft: b.mutation<
      { draft: PtoPprTkDraft },
      { projectId: string; kind: string; title: string; work_type: string }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/ppr-tk-drafts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoPprTkDraft'],
    }),
    /** Получить черновик ППР/ТК с разделами (M9). */
    getPtoPprTkDraft: b.query<{ draft: PtoPprTkDraft }, string>({
      query: (id) => `/agents/pto/ppr-tk-drafts/${id}`,
      providesTags: ['PtoPprTkDraft'],
    }),
    /**
     * Выгрузить черновик ППР/ТК в DOCX (M9). Лениво, по клику. `clean` —
     * чистовой без служебных пометок: только у утверждённого аудитором (иначе 409).
     */
    exportPtoPprTkDraft: b.query<Blob, { id: string; clean?: boolean }>({
      query: ({ id, clean }) => ({
        url: `/agents/pto/ppr-tk-drafts/${id}/docx${clean ? '?clean=true' : ''}`,
        cache: 'no-store',
        responseHandler: fileResponse,
      }),
    }),
    /** Удалить черновик ППР/ТК (M9). */
    deletePtoPprTkDraft: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/pto/ppr-tk-drafts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PtoPprTkDraft'],
    }),
    /** Заменить текст раздела черновика ручной правкой (M9 доводка). */
    updatePtoPprTkSection: b.mutation<
      { draft: PtoPprTkDraft },
      { draftId: string; key: string; content: string }
    >({
      query: ({ draftId, key, content }) => ({
        url: `/agents/pto/ppr-tk-drafts/${draftId}/sections/${key}`,
        method: 'PATCH',
        body: { content },
      }),
      invalidatesTags: ['PtoPprTkDraft'],
    }),
    /**
     * Пересобрать один раздел черновика заново (M9 доводка). `hint` — что
     * исправить (режим rd: раздел уходит в очередь, затем снова аудит).
     */
    /** Рисунки черновика ППР/ТК — графическая часть (§8.1), без байтов. */
    listPtoPprFigures: b.query<{ items: PtoPprFigure[] }, string>({
      query: (draftId) => `/agents/pto/ppr-tk-drafts/${draftId}/figures`,
      providesTags: ['PtoPprFigure'],
    }),
    /** Приложить рисунки к разделу (multipart: `file` + `caption`). PDF — по рисунку на лист. */
    uploadPtoPprFigure: b.mutation<
      { figures: PtoPprFigure[] },
      { draftId: string; key: string; form: FormData }
    >({
      query: ({ draftId, key, form }) => ({
        url: `/agents/pto/ppr-tk-drafts/${draftId}/sections/${key}/figures`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['PtoPprFigure'],
    }),
    /** Сменить подпись рисунка. */
    updatePtoPprFigure: b.mutation<
      { figure: PtoPprFigure },
      { draftId: string; id: string; caption: string }
    >({
      query: ({ draftId, id, caption }) => ({
        url: `/agents/pto/ppr-tk-drafts/${draftId}/figures/${id}`,
        method: 'PATCH',
        body: { caption },
      }),
      invalidatesTags: ['PtoPprFigure'],
    }),
    /** Удалить рисунок. */
    deletePtoPprFigure: b.mutation<{ ok: boolean }, { draftId: string; id: string }>({
      query: ({ draftId, id }) => ({
        url: `/agents/pto/ppr-tk-drafts/${draftId}/figures/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PtoPprFigure'],
    }),
    /**
     * Перестроить схемы графической части по РД без перегенерации текста
     * (схема производства работ по плану свай); итог — в расчётах паспорта.
     */
    buildPtoPprSchemes: b.mutation<{ draft: PtoPprTkDraft; figures: PtoPprFigure[] }, string>({
      query: (draftId) => ({
        url: `/agents/pto/ppr-tk-drafts/${draftId}/schemes`,
        method: 'POST',
      }),
      invalidatesTags: ['PtoPprFigure', 'PtoPprTkDraft'],
    }),
    /** Байты рисунка как Blob — превью (RBAC через Authorization). Рисунок по id не меняется. */
    getPtoPprFigureImage: b.query<Blob, { draftId: string; id: string }>({
      query: ({ draftId, id }) => ({
        url: `/agents/pto/ppr-tk-drafts/${draftId}/figures/${id}/image`,
        responseHandler: (response) => response.blob(),
      }),
    }),
    regeneratePtoPprTkSection: b.mutation<
      { draft: PtoPprTkDraft },
      { draftId: string; key: string; hint?: string }
    >({
      query: ({ draftId, key, hint }) => ({
        url: `/agents/pto/ppr-tk-drafts/${draftId}/sections/${key}/regenerate`,
        method: 'POST',
        body: hint ? { hint } : {},
      }),
      invalidatesTags: ['PtoPprTkDraft'],
    }),
    /**
     * Новый ППР по РД и технике (режим rd): черновик встаёт в очередь
     * (`gen_status=queued`); новая техника запоминается в справочнике.
     */
    generatePtoPprRdDraft: b.mutation<
      { draft: PtoPprTkDraft },
      { projectId: string } & PtoPprGenerateBody
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/ppr-tk-drafts/generate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoPprTkDraft', 'PtoEquipment'],
    }),
    /** Перезапустить всю генерацию черновика rd (разделы, правленные инженером, сохраняются). */
    restartPtoPprTkDraftGeneration: b.mutation<{ draft: PtoPprTkDraft }, string>({
      query: (id) => ({ url: `/agents/pto/ppr-tk-drafts/${id}/generate`, method: 'POST' }),
      invalidatesTags: ['PtoPprTkDraft'],
    }),
    /** Новый цикл агента-аудитора «аудит → исправление» по черновику rd. */
    reviewPtoPprTkDraft: b.mutation<{ draft: PtoPprTkDraft }, string>({
      query: (id) => ({ url: `/agents/pto/ppr-tk-drafts/${id}/review`, method: 'POST' }),
      invalidatesTags: ['PtoPprTkDraft'],
    }),
    /** Ревизии РД проекта для мастера «ППР по РД»: индексация, фрагменты, позиции объёма. */
    listPtoPprRdSources: b.query<{ items: PtoPprRdSource[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/ppr-rd-sources`,
      providesTags: ['PtoRdRevision'],
    }),
    /** Справочник техники организации (общий для всех проектов ПТО). */
    listPtoEquipment: b.query<{ items: PtoEquipment[] }, void>({
      query: () => '/agents/pto/equipment',
      providesTags: ['PtoEquipment'],
    }),
    createPtoEquipment: b.mutation<{ equipment: PtoEquipment }, PtoEquipmentBody>({
      query: (body) => ({ url: '/agents/pto/equipment', method: 'POST', body }),
      invalidatesTags: ['PtoEquipment'],
    }),
    updatePtoEquipment: b.mutation<
      { equipment: PtoEquipment },
      { id: string } & PtoEquipmentBody
    >({
      query: ({ id, ...body }) => ({ url: `/agents/pto/equipment/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['PtoEquipment'],
    }),
    /** Удалить технику из справочника (мягко; в уже составленных ППР — снимок). */
    deletePtoEquipment: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/pto/equipment/${id}`, method: 'DELETE' }),
      invalidatesTags: ['PtoEquipment'],
    }),
    /**
     * Перечень техники файлом (multipart, поле `file`: .xlsx/.xlsm/.csv,
     * ≤ 10 МБ; книгу Excel 97–2003 сервер не принимает): разбор и дополнение
     * недостающих характеристик ИИ с проверкой вторым, проверочным ИИ — до 2–4
     * минут. НИЧЕГО не сохраняет (тегов не трогает): инженер проверяет строки и
     * добавляет их в мастер или в справочник.
     */
    importPtoEquipmentFile: b.mutation<{ import: PtoEquipmentImport }, FormData>({
      query: (form) => ({ url: '/agents/pto/equipment/import', method: 'POST', body: form }),
    }),
    /**
     * Перечень в справочник (до 200 записей): новые создаются, у записей с тем же
     * наименованием дозаполняются только пустые поля. Ошибка записи не роняет
     * запрос — она в `results` (в порядке `items`).
     */
    savePtoEquipmentBulk: b.mutation<
      { results: PtoEquipmentBulkResult[] },
      { items: PtoEquipmentBody[] }
    >({
      query: (body) => ({ url: '/agents/pto/equipment/bulk', method: 'POST', body }),
      invalidatesTags: ['PtoEquipment'],
    }),
    /** Список сгенерированных актов ИД проекта (M4). */
    listPtoExecutiveActs: b.query<
      { items: PtoExecutiveAct[] },
      { projectId: string; deleted?: boolean }
    >({
      query: ({ projectId, deleted }) =>
        `/agents/pto/projects/${projectId}/executive-acts${deleted ? '?deleted=true' : ''}`,
      providesTags: ['PtoExecAct'],
    }),
    /** Один акт ИД — в том числе помеченный на удаление (`deleted_at`); 404 —
     *  акт вычищен из корзины. Карточка заявки Прораба так узнаёт, что акт заявки
     *  удалён и АОСР можно пересобрать (если сервер не прислал признак сам). */
    getPtoExecutiveAct: b.query<{ act: PtoExecutiveAct }, string>({
      query: (id) => `/agents/pto/executive-acts/${id}`,
      providesTags: ['PtoExecAct'],
    }),
    // Удаление/восстановление акта меняет и заявки Прораба: признак «акт удалён»
    // приходит в их списке — без 'Field' карточка заявки 5 минут жила бы старым.
    deletePtoExecutiveAct: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/pto/executive-acts/${id}/delete`, method: 'POST' }),
      invalidatesTags: ['PtoExecAct', 'Field'],
    }),
    restorePtoExecutiveAct: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/pto/executive-acts/${id}/restore`, method: 'POST' }),
      invalidatesTags: ['PtoExecAct', 'Field'],
    }),
    /** Сгенерировать акт ИД по выбранным позициям объёма (M4). */
    generatePtoAct: b.mutation<
      { act: PtoExecutiveAct },
      {
        projectId: string;
        kind?: string;
        form_ref?: string;
        act_no?: string;
        act_date?: string;
        object_name?: string;
        object_address?: string;
        parties?: Array<{ role: string; text: string }>;
        representatives?: Array<{ role: string; details: string; short_name?: string }>;
        representative_ids?: string[];
        material_ids?: string[];
        template_id?: string;
        performer?: string;
        work_name: string;
        position_ids?: string[];
        rd_docs?: string;
        materials?: Array<{ name: string; quality_docs?: string }>;
        materials_registry_ref?: string;
        compliance_docs?: string;
        date_start?: string;
        date_end?: string;
        norms?: string;
        next_works?: string;
        extra?: string;
        appendices?: string;
        copies?: number;
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/acts/generate`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoExecAct'],
    }),
    /** Сменить статус акта ИД: draft|on_signing|signed|submitted (M4). */
    setPtoActStatus: b.mutation<{ ok: boolean }, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/agents/pto/executive-acts/${id}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['PtoExecAct'],
    }),
    /** Выгрузка DOCX сгенерированного акта ИД (M4). Лениво, по клику. */
    exportPtoActDocx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/pto/executive-acts/${id}/docx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),
    /** Список накопительных ведомостей СМР проекта (M13). */
    listPtoSmrRegisters: b.query<{ items: PtoSmrRegister[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/smr-registers`,
      providesTags: ['PtoSmrRegister'],
    }),
    /** Импорт ведомости СМР из .xlsx (multipart: title + файл) (M13). */
    importPtoSmrRegister: b.mutation<
      { register: PtoSmrRegister },
      { projectId: string; form: FormData }
    >({
      query: ({ projectId, form }) => ({
        url: `/agents/pto/projects/${projectId}/smr-registers/import`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['PtoSmrRegister'],
    }),
    /** Шапка ведомости СМР (M13). */
    getPtoSmrRegister: b.query<{ register: PtoSmrRegister }, string>({
      query: (id) => `/agents/pto/smr-registers/${id}`,
    }),
    /** Строки ведомости СМР (M13). */
    listPtoSmrItems: b.query<{ items: PtoSmrItem[] }, string>({
      query: (id) => `/agents/pto/smr-registers/${id}/items`,
      providesTags: ['PtoSmrItem'],
    }),
    /** Правка строки ведомости (корректировка при ошибке) (M13). */
    updatePtoSmrItem: b.mutation<
      { item: PtoSmrItem | null },
      { id: string; patch: Partial<Omit<PtoSmrItem, 'id' | 'register_id' | 'project_id' | 'row_no' | 'act_id' | 'responsible' | 'source_ref' | 'raw' | 'created_by' | 'created_at'>> }
    >({
      query: ({ id, patch }) => ({
        url: `/agents/pto/smr-registers/items/${id}`,
        method: 'POST',
        body: patch,
      }),
      invalidatesTags: ['PtoSmrItem'],
    }),
    /** Дополнить ведомость повторным импортом .xlsx (sync) (M13, срез B). */
    syncPtoSmrRegister: b.mutation<
      { register: PtoSmrRegister },
      { id: string; form: FormData }
    >({
      query: ({ id, form }) => ({
        url: `/agents/pto/smr-registers/${id}/sync`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['PtoSmrRegister', 'PtoSmrItem'],
    }),
    /** Пересвязать строки с актами АОСР по номеру (M13, срез B). */
    relinkPtoSmrActs: b.mutation<{ linked: number }, string>({
      query: (id) => ({ url: `/agents/pto/smr-registers/${id}/relink-acts`, method: 'POST' }),
      invalidatesTags: ['PtoSmrItem'],
    }),
    /** Привязать/отвязать строку к акту вручную (M13, срез B). act_id=null — отвязать. */
    setPtoSmrItemAct: b.mutation<
      { item: PtoSmrItem | null },
      { id: string; act_id: string | null }
    >({
      query: ({ id, act_id }) => ({
        url: `/agents/pto/smr-registers/items/${id}/act`,
        method: 'POST',
        body: { act_id },
      }),
      invalidatesTags: ['PtoSmrItem'],
    }),
    /** Реестр назначенных приказами представителей проекта (M4). */
    listPtoOrderReps: b.query<{ items: PtoOrderRepresentative[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/order-representatives`,
      providesTags: ['PtoOrderRep'],
    }),
    /** Добавить представителя в реестр приказов (M4). */
    createPtoOrderRep: b.mutation<
      { representative: PtoOrderRepresentative },
      {
        projectId: string;
        role: string;
        position?: string;
        full_name: string;
        nrs?: string;
        order_ref?: string;
        organization?: string;
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/order-representatives`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoOrderRep'],
    }),
    /** Удалить представителя из реестра приказов (M4). */
    deletePtoOrderRep: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/pto/order-representatives/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PtoOrderRep'],
    }),
    /** Реквизиты шапки АОСР проекта (КR9, `metadata.aosr_requisites`): стороны,
     *  объект с адресом, представители по умолчанию. Их берут форма «Актов ИД» и
     *  сборка АОСР по заявке Прораба. Не заполнены — `requisites` null или пустые
     *  поля; `fallback` — что мост возьмёт из реквизитов ОЖР вместо пустых. */
    getPtoAosrRequisites: b.query<PtoAosrRequisitesResponse, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/aosr/requisites`,
      providesTags: ['PtoAosrRequisites'],
    }),
    /** Сохранить реквизиты шапки АОСР проекта (запись в проект ПТО; проект в
     *  корзине — 409): пришедшие ключи заменяют сохранённые (пустая строка —
     *  очистить), неприсланные остаются. */
    setPtoAosrRequisites: b.mutation<
      PtoAosrRequisitesResponse,
      { projectId: string; requisites: PtoAosrRequisites }
    >({
      query: ({ projectId, requisites }) => ({
        url: `/agents/pto/projects/${projectId}/aosr/requisites`,
        method: 'PATCH',
        body: { requisites },
      }),
      invalidatesTags: ['PtoAosrRequisites', 'PtoProject'],
    }),
    /** Реестр материалов с документами качества проекта (M4). */
    listPtoQualityMaterials: b.query<{ items: PtoQualityMaterial[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/quality-materials`,
      providesTags: ['PtoQualityMat'],
    }),
    /** Добавить материал с документами качества в реестр (M4). */
    createPtoQualityMaterial: b.mutation<
      { material: PtoQualityMaterial },
      { projectId: string; name: string; quality_docs?: string }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/quality-materials`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoQualityMat'],
    }),
    /** Удалить материал из реестра качества (M4). */
    deletePtoQualityMaterial: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/pto/quality-materials/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PtoQualityMat'],
    }),
    /** Автоматически пополнить реестр материалов и документов качества из РД и ТТН. */
    syncPtoQualityMaterialsFromRd: b.mutation<
      { added: number; from_ttn: number; from_rd: number; items: PtoQualityMaterial[] },
      string
    >({
      query: (projectId) => ({
        url: `/agents/pto/projects/${projectId}/quality-materials/sync-from-rd`,
        method: 'POST',
      }),
      invalidatesTags: ['PtoQualityMat'],
    }),
    /** Запустить сверку объёмов (M3): mode = plan_fact | gp_sp. */
    runPtoVolumeCheck: b.mutation<
      { check: PtoVolumeCheck },
      { projectId: string; mode: string }
    >({
      query: ({ projectId, mode }) => ({
        url: `/agents/pto/projects/${projectId}/volume-checks`,
        method: 'POST',
        body: { mode },
      }),
      invalidatesTags: ['PtoVolumeCheck'],
    }),
    /** Список прогонов сверки объёмов проекта (M3). */
    listPtoVolumeChecks: b.query<{ items: PtoVolumeCheck[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/volume-checks`,
      providesTags: ['PtoVolumeCheck'],
    }),
    /** Прогон сверки + его расхождения (M3). */
    getPtoVolumeCheck: b.query<
      { check: PtoVolumeCheck; discrepancies: PtoVolumeDiscrepancy[] },
      string
    >({
      query: (id) => `/agents/pto/volume-checks/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'PtoVolumeDiscrepancy', id }],
    }),
    /** HITL-разметка расхождения: accepted | rejected | disputed (M3). */
    annotatePtoDiscrepancy: b.mutation<
      { ok: boolean },
      { id: string; checkId: string; decision: string; comment?: string }
    >({
      query: ({ id, decision, comment }) => ({
        url: `/agents/pto/volume-discrepancies/${id}/annotate`,
        method: 'POST',
        body: { decision, comment },
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: 'PtoVolumeDiscrepancy', id: arg.checkId }],
    }),
    /** Создать заявку (M6): lab|lnk|mto|zhvk|geo. */
    createPtoRequest: b.mutation<
      { request: PtoRequest },
      {
        projectId: string;
        category: string;
        subject: string;
        body?: string;
        related_document_id?: string;
        due_date?: string;
        responsible?: string;
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/requests`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoRequest'],
    }),
    /** Реестр заявок проекта (M6): внутренние заявки ПТО + сквозные заявки + заявки на освидетельствование. */
    listPtoRequests: b.query<
      {
        items: PtoRequest[];
        function_requests?: FunctionRequest[];
        inspection_requests?: FieldInspectionRequest[];
      },
      string
    >({
      query: (projectId) => `/agents/pto/projects/${projectId}/requests`,
      providesTags: ['PtoRequest', 'Field'],
    }),
    /** Сменить статус заявки: open|in_progress|closed (M6). */
    setPtoRequestStatus: b.mutation<{ ok: boolean }, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/agents/pto/requests/${id}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['PtoRequest'],
    }),
    /** Выгрузка XLSX-реестра заявок проекта (M6). Лениво, по клику. */
    exportPtoRequestsXlsx: b.query<Blob, string>({
      query: (projectId) => ({
        url: `/agents/pto/projects/${projectId}/requests-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),
    /** Список чек-листов комплектности ИРД проекта (M10). */
    listPtoChecklists: b.query<{ items: PtoChecklist[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/checklists`,
      providesTags: ['PtoChecklist'],
    }),
    /** Создать чек-лист комплектности ИРД (опц. с пунктами) (M10). */
    createPtoChecklist: b.mutation<
      { checklist: PtoChecklist },
      {
        projectId: string;
        title: string;
        object_type?: string;
        note?: string;
        items?: Array<{ name: string; code?: string; required?: boolean; note?: string }>;
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/checklists`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoChecklist'],
    }),
    /** Встроенные типовые шаблоны чек-листов ИРД (M10). */
    listPtoChecklistTemplates: b.query<{ items: PtoChecklistTemplate[] }, void>({
      query: () => `/agents/pto/checklist-templates`,
    }),
    /** Создать чек-лист из встроенного типового шаблона ИРД (M10). */
    createPtoChecklistFromTemplate: b.mutation<
      { checklist: PtoChecklist },
      { projectId: string; template_key: string; title?: string }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/checklists/from-template`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoChecklist'],
    }),
    /** Чек-лист с пунктами и расчётом готовности (M10). */
    getPtoChecklist: b.query<{ detail: PtoChecklistDetail }, string>({
      query: (id) => `/agents/pto/checklists/${id}`,
      providesTags: ['PtoChecklist'],
    }),
    /** Сменить статус чек-листа (M10). */
    setPtoChecklistStatus: b.mutation<{ ok: boolean }, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/agents/pto/checklists/${id}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['PtoChecklist'],
    }),
    /** Добавить пункты в чек-лист (M10). */
    addPtoChecklistItems: b.mutation<
      { ok: boolean },
      {
        id: string;
        items: Array<{ name: string; code?: string; required?: boolean; note?: string }>;
      }
    >({
      query: ({ id, items }) => ({
        url: `/agents/pto/checklists/${id}/items`,
        method: 'POST',
        body: { items },
      }),
      invalidatesTags: ['PtoChecklist'],
    }),
    /** Отметить наличие документа в пункте (HITL) (M10). */
    setPtoChecklistItemStatus: b.mutation<{ ok: boolean }, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/agents/pto/checklist-items/${id}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['PtoChecklist'],
    }),
    /** Ввести срок действия документа пункта (+ напоминание) (M10). */
    setPtoChecklistItemValidity: b.mutation<
      { ok: boolean },
      {
        id: string;
        valid_until?: string;
        source_ref?: string;
        page_number?: number;
        notice_days?: number;
        confidence?: number;
        needs_review?: boolean;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/pto/checklist-items/${id}/validity`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoChecklist'],
    }),
    /** Выгрузка XLSX-реестра комплектности (M10). Лениво, по клику. */
    exportPtoCompletenessXlsx: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/pto/checklists/${id}/completeness-xlsx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),
    /** Список шаблонов писем/заявок (M10). */
    listPtoLetterTemplates: b.query<{ items: PtoLetterTemplate[] }, void>({
      query: () => `/agents/pto/letter-templates`,
      providesTags: ['PtoLetterTemplate'],
    }),
    /** Загрузить шаблон письма (one-active-per-kind) (M10). */
    createPtoLetterTemplate: b.mutation<
      { template: PtoLetterTemplate },
      { kind: string; name: string; body_template: string }
    >({
      query: (body) => ({
        url: `/agents/pto/letter-templates`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoLetterTemplate'],
    }),
    /** Сгенерировать письмо/заявку из шаблона (DOCX) (M10). Лениво. */
    renderPtoLetterDocx: b.query<
      Blob,
      { projectId: string; kind: string; recipient?: string; signer?: string; date?: string }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/letters-docx`,
        method: 'POST',
        body,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),
    /** Список запросов SQ проекта (M8). */
    listPtoSq: b.query<{ items: PtoRequest[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/sq`,
      providesTags: ['PtoSq'],
    }),
    /** Создать запрос SQ в проектный институт (M8). */
    createPtoSq: b.mutation<
      { request: PtoRequest },
      { projectId: string; subject: string; body?: string; related_document_id?: string }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/sq`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoSq'],
    }),
    /** Автоматически сформировать запрос SQ по замечаниям входного контроля РД. */
    createAutoPtoSqFromRd: b.mutation<
      /** `gap_findings` — сколько принятых находок анализа комплектности (№7)
       *  вошло разделом «Возможно не учтено в РД». */
      { request: PtoRequest; issues_count: number; gap_findings?: number },
      { projectId: string; document_id?: string }
    >({
      query: ({ projectId, document_id }) => ({
        url: `/agents/pto/projects/${projectId}/sq/auto-from-rd`,
        method: 'POST',
        body: document_id ? { document_id } : {},
      }),
      invalidatesTags: ['PtoSq', 'PtoRdGaps'],
    }),
    /** Зафиксировать ответ на запрос SQ (закрывает запрос) (M8). */
    answerPtoSq: b.mutation<{ ok: boolean }, { id: string; answer: string }>({
      query: ({ id, answer }) => ({
        url: `/agents/pto/sq/${id}/answer`,
        method: 'POST',
        body: { answer },
      }),
      invalidatesTags: ['PtoSq'],
    }),
    /** Протокол изменений РД документа: активная ↔ предыдущая ревизия (M8). */
    getRdChangeProtocol: b.query<{ protocol: RdChangeProtocol }, string>({
      query: (documentId) => `/agents/pto/rd-documents/${documentId}/change-protocol`,
    }),
    /** Список приёмочных инспекций проекта (M12). */
    listPtoInspections: b.query<{ items: PtoInspection[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/inspections`,
      providesTags: ['PtoInspection'],
    }),
    /** Запланировать приёмочную инспекцию (M12). */
    createPtoInspection: b.mutation<
      { inspection: PtoInspection },
      { projectId: string; kind?: string; title: string; scheduled_date?: string }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/inspections`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoInspection'],
    }),
    /** Зафиксировать результат инспекции: planned|passed|failed (M12). */
    setPtoInspectionResult: b.mutation<
      { ok: boolean },
      { id: string; status: string; note?: string }
    >({
      query: ({ id, status, note }) => ({
        url: `/agents/pto/inspections/${id}/result`,
        method: 'POST',
        body: { status, note },
      }),
      invalidatesTags: ['PtoInspection'],
    }),
    /** Список комплектов ИД проекта (M12). */
    listPtoIdPackages: b.query<{ items: PtoIdPackage[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/id-packages`,
      providesTags: ['PtoIdPackage'],
    }),
    /** Собрать комплект ИД из выбранных актов (M12). */
    createPtoIdPackage: b.mutation<
      { package: PtoIdPackage },
      { projectId: string; title: string; act_ids: string[]; note?: string }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/agents/pto/projects/${projectId}/id-packages`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PtoIdPackage'],
    }),
    /** Комплект ИД с раскрытыми актами и готовностью (M12). */
    getPtoIdPackage: b.query<{ detail: PackageDetail }, string>({
      query: (id) => `/agents/pto/id-packages/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'PtoIdPackage', id }],
    }),
    /** Сменить статус комплекта ИД (M12). */
    setPtoIdPackageStatus: b.mutation<
      { ok: boolean },
      { id: string; status: string; stdo_ref?: string }
    >({
      query: ({ id, status, stdo_ref }) => ({
        url: `/agents/pto/id-packages/${id}/status`,
        method: 'POST',
        body: { status, stdo_ref },
      }),
      invalidatesTags: (_r, _e, arg) => ['PtoIdPackage', { type: 'PtoIdPackage', id: arg.id }],
    }),
    listPtoChatSessions: b.query<{ items: PtoChatSession[] }, string>({
      query: (projectId) => `/agents/pto/projects/${projectId}/chat/sessions`,
      providesTags: ['PtoChatSession'],
    }),
    startPtoChatSession: b.mutation<
      { session: PtoChatSession },
      { projectId: string; title?: string }
    >({
      query: ({ projectId, title }) => ({
        url: `/agents/pto/projects/${projectId}/chat/sessions`,
        method: 'POST',
        body: { title },
      }),
      invalidatesTags: ['PtoChatSession'],
    }),
    getPtoChatSession: b.query<
      { session: PtoChatSession; messages: PtoChatMessage[] },
      string
    >({
      query: (id) => `/agents/pto/chat/sessions/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'PtoChatMessages', id }],
    }),
    sendPtoChatMessage: b.mutation<
      { message: PtoChatMessage },
      { sessionId: string; content: string }
    >({
      query: ({ sessionId, content }) => ({
        url: `/agents/pto/chat/sessions/${sessionId}/messages`,
        method: 'POST',
        body: { content },
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'PtoChatMessages', id: arg.sessionId },
        'PtoChatSession',
      ],
    }),
    deletePtoChatSession: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/agents/pto/chat/sessions/${id}`, method: 'DELETE' }),
      invalidatesTags: ['PtoChatSession'],
    }),

    // ===================== ОСНОВА.МТО =====================
    // Материально-техническое обеспечение (каркас, этап 0): карточка агента
    // и метрики-заглушки. Бизнес-логики пока нет — только витрина дашборда.

  }),
  overrideExisting: false,
});

export const {
  useGetPtoInfoQuery,
  useListPtoProjectsQuery,
  useCreatePtoProjectMutation,
  useGetPtoProjectQuery,
  useArchivePtoProjectMutation,
  useDeletePtoProjectMutation,
  useRestorePtoProjectMutation,
  useListPtoMembersQuery,
  useAddPtoMemberMutation,
  useListPtoRdDocumentsQuery,
  useCreatePtoRdDocumentMutation,
  useDeletePtoRdDocumentMutation,
  useRestorePtoRdDocumentMutation,
  useUploadPtoRevisionMutation,
  useListPtoRevisionsQuery,
  useGetPtoRevisionQuery,
  useReindexPtoRevisionMutation,
  useDeletePtoRevisionMutation,
  useRestorePtoRevisionMutation,
  useRefreshPtoProjectSectionsMutation,
  useListPtoVolumesQuery,
  useGetPtoVolumesSummaryQuery,
  useExtractPtoVolumesMutation,
  useGetPtoRdCarryOverQuery,
  useApplyPtoRdCarryOverMutation,
  useAnnotatePtoVolumeMutation,
  useListPtoActsQuery,
  useRecordPtoExecutedMutation,
  useLazyExportPtoReportXlsxQuery,
  useLazyExportPtoMaterialsVedomostQuery,
  useListPtoExecutiveActsQuery,
  useGetPtoExecutiveActQuery,
  useDeletePtoExecutiveActMutation,
  useRestorePtoExecutiveActMutation,
  useGeneratePtoActMutation,
  useSetPtoActStatusMutation,
  useLazyExportPtoActDocxQuery,
  useListPtoSmrRegistersQuery,
  useImportPtoSmrRegisterMutation,
  useGetPtoSmrRegisterQuery,
  useListPtoSmrItemsQuery,
  useUpdatePtoSmrItemMutation,
  useSyncPtoSmrRegisterMutation,
  useRelinkPtoSmrActsMutation,
  useSetPtoSmrItemActMutation,
  useListPtoOrderRepsQuery,
  useCreatePtoOrderRepMutation,
  useDeletePtoOrderRepMutation,
  useGetPtoAosrRequisitesQuery,
  useSetPtoAosrRequisitesMutation,
  useListPtoQualityMaterialsQuery,
  useCreatePtoQualityMaterialMutation,
  useDeletePtoQualityMaterialMutation,
  useSyncPtoQualityMaterialsFromRdMutation,
  useListPtoActTemplatesQuery,
  useUploadPtoActTemplateMutation,
  useDeletePtoActTemplateMutation,
  useListPtoPprTkQuery,
  useUploadPtoPprTkMutation,
  useLazySearchPtoPprTkQuery,
  useDeletePtoPprTkMutation,
  useListPtoPprTkDraftsQuery,
  useCreatePtoPprTkDraftMutation,
  useGetPtoPprTkDraftQuery,
  useLazyExportPtoPprTkDraftQuery,
  useDeletePtoPprTkDraftMutation,
  useUpdatePtoPprTkSectionMutation,
  useRegeneratePtoPprTkSectionMutation,
  useListPtoPprFiguresQuery,
  useUploadPtoPprFigureMutation,
  useUpdatePtoPprFigureMutation,
  useDeletePtoPprFigureMutation,
  useGetPtoPprFigureImageQuery,
  useBuildPtoPprSchemesMutation,
  useGeneratePtoPprRdDraftMutation,
  useRestartPtoPprTkDraftGenerationMutation,
  useReviewPtoPprTkDraftMutation,
  useListPtoPprRdSourcesQuery,
  useListPtoEquipmentQuery,
  useCreatePtoEquipmentMutation,
  useUpdatePtoEquipmentMutation,
  useDeletePtoEquipmentMutation,
  useImportPtoEquipmentFileMutation,
  useSavePtoEquipmentBulkMutation,
  useRunPtoVolumeCheckMutation,
  useListPtoVolumeChecksQuery,
  useGetPtoVolumeCheckQuery,
  useAnnotatePtoDiscrepancyMutation,
  useCreatePtoRequestMutation,
  useListPtoRequestsQuery,
  useSetPtoRequestStatusMutation,
  useLazyExportPtoRequestsXlsxQuery,
  useListPtoChecklistsQuery,
  useCreatePtoChecklistMutation,
  useListPtoChecklistTemplatesQuery,
  useCreatePtoChecklistFromTemplateMutation,
  useGetPtoChecklistQuery,
  useSetPtoChecklistStatusMutation,
  useAddPtoChecklistItemsMutation,
  useSetPtoChecklistItemStatusMutation,
  useSetPtoChecklistItemValidityMutation,
  useLazyExportPtoCompletenessXlsxQuery,
  useListPtoLetterTemplatesQuery,
  useCreatePtoLetterTemplateMutation,
  useLazyRenderPtoLetterDocxQuery,
  useListPtoSqQuery,
  useCreatePtoSqMutation,
  useCreateAutoPtoSqFromRdMutation,
  useAnswerPtoSqMutation,
  useGetRdChangeProtocolQuery,
  useListPtoInspectionsQuery,
  useCreatePtoInspectionMutation,
  useSetPtoInspectionResultMutation,
  useListPtoIdPackagesQuery,
  useCreatePtoIdPackageMutation,
  useGetPtoIdPackageQuery,
  useSetPtoIdPackageStatusMutation,
  useListPtoChatSessionsQuery,
  useStartPtoChatSessionMutation,
  useGetPtoChatSessionQuery,
  useSendPtoChatMessageMutation,
  useDeletePtoChatSessionMutation,
} = ptoApi;
